import { designVariables } from "@studio3t/design-variables";
import styled from "styled-components";
import type { StageRole } from "./pipelineLint";
import { usePipelineStaging } from "./pipelineStaging";
import {
  StageArrowFilledIcon,
  StageArrowIcon,
  StageCompletedIcon,
  StageNotCompletedIcon,
} from "./sectionIcons";
import { type StageProgress, stageProgressFromStaged } from "./stageProgress";

const SEMANTIC = designVariables.ui.semantic;
const BTN = designVariables.ui.button;
const SINGLE_LINE = designVariables.type.singleLine;

const STEPS: ReadonlyArray<{ id: StageRole; label: string }> = [
  { id: "source", label: "Source" },
  { id: "transform", label: "Transform" },
  { id: "sink", label: "Sink" },
];

// Which steps of the open pipeline are done: the staged rows in the Stages
// view; in the Full pipeline view the code editor's last settled lint pass
// (pipelineCodeProgress — filled AND lint-clean per role), which holds while
// the next pass is pending. Before the first pass reports, the staged rows
// stand in: the code was generated from them, and a draft over that code
// can only exist while staging hasn't changed.
function useStageProgress(): StageProgress {
  const { studioView, sources, transforms, sinks, pipelineCodeProgress } =
    usePipelineStaging();
  const fromStaged = stageProgressFromStaged({ sources, transforms, sinks });
  if (studioView !== "full") return fromStaged;
  return pipelineCodeProgress ?? fromStaged;
}

// The toolbar's middle: Source → Transform → Sink (Figma Bridge_Stages,
// 14896-92090, all done 14895-206043), each step a 12px marker and its
// label, a connector between neighbours. A completed step reads in the
// heading color, the rest in the disabled one; a connector is dashed
// until the steps on both of its ends are completed, then solid.
export function StageProgressBar() {
  const progress = useStageProgress();
  return (
    <Steps data-testid="stage-progress">
      {STEPS.map((step, index) => (
        <Step
          key={step.id}
          $completed={progress[step.id]}
          data-testid={`stage-progress-${step.id}`}
          data-completed={progress[step.id]}
        >
          {progress[step.id] ? (
            <StageCompletedIcon />
          ) : (
            <StageNotCompletedIcon />
          )}
          {step.label}
          {index < STEPS.length - 1 && (
            <StepConnector
              filled={progress[step.id] && progress[STEPS[index + 1].id]}
            />
          )}
        </Step>
      ))}
    </Steps>
  );
}

function StepConnector({ filled }: { filled: boolean }) {
  return (
    <Connector $filled={filled} data-filled={filled}>
      {filled ? <StageArrowFilledIcon /> : <StageArrowIcon />}
    </Connector>
  );
}

const Steps = styled.div`
  display: inline-flex;
  align-items: center;
`;

// Figma Bridge_Stages: 28px tall, spacingxs (5px) padding and gap,
// Single-line/Small/Medium.
const Step = styled.div<{ $completed: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${SEMANTIC.spacingxs}px;
  height: ${BTN.s.size}px;
  padding: 0 ${SEMANTIC.spacingxs}px;
  color: ${(p) =>
    p.$completed ? p.theme.semanticTextHeading : p.theme.semanticTextDisabled};
  font-size: ${SINGLE_LINE.small.medium.fontSize}px;
  font-weight: ${SINGLE_LINE.small.medium.fontWeight};
  line-height: 1.2;
  white-space: nowrap;
`;

// Figma: the dashed arrow in borderdark (#E2E2E2), the solid one in
// textlight (#696969).
const Connector = styled.span<{ $filled: boolean }>`
  display: inline-flex;
  color: ${(p) =>
    p.$filled ? p.theme.semanticTextLight : p.theme.semanticBorderDark};
`;
